// Image-driven parametric model pipeline.
//
// Two-stage flow used when the user uploads a reference image:
//
//   Stage 1: gpt-image-2/edit
//     in:  user photo
//     out: synthesized "technical sheet" — top/front/side/iso views of the
//          object on a clean background. Forces the LLM in stage 2 to reason
//          about geometry from multiple angles instead of guessing from a
//          single perspective shot.
//
//   Stage 2: gpt-5.5 vision (openrouter/router via callFalVision)
//     in:  user photo + multi-view sheet + node registry + user's text prompt
//     out: <actions> JSON — graph mutation tool calls (add_node, connect_wire, …)
//          plus <reply> for the user.
//
// Same FAL key for both stages. Caller invokes onStage(stage, payload?) for
// progress UI updates.

import { callFalImageEdit, callFalVision, parseModelOutput, fileToDataUri } from './falProvider';
import type { ToolCall } from './falProvider';
import { VISION_SYSTEM_PROMPT } from './graphTools';

export type PipelineStage =
  | { kind: 'start' }
  | { kind: 'multiview-pending' }
  | { kind: 'multiview-done'; sheetDataUri: string; sheetUrl: string }
  | { kind: 'synthesis-pending' }
  | { kind: 'synthesis-done'; actions: ToolCall[]; reply: string }
  | { kind: 'error'; stage: 'multiview' | 'synthesis'; message: string };

export interface VisionPipelineRequest {
  apiKey: string;
  model: string;                 // e.g. "openai/gpt-5.5"
  userPrompt: string;            // user's text alongside the image (Turkish)
  imageFile: File;               // user-uploaded reference photo
  conversationContext?: string;  // optional prior chat turns for continuity
  onStage: (s: PipelineStage) => void;
}

/** Stage-1 prompt fed to gpt-image-2/edit. Engineered to coax the model into
 *  producing a flat, white-background, line-art technical sheet that a vision
 *  LLM can reason about. Avoids photoreal noise that would distract stage 2. */
const MULTIVIEW_PROMPT = `Generate a clean technical orthographic drawing sheet showing this object from FOUR labeled views arranged in a 2x2 grid:

TOP-LEFT: FRONT view (elevation, viewer-facing)
TOP-RIGHT: SIDE view (90° rotated)
BOTTOM-LEFT: TOP view (looking straight down)
BOTTOM-RIGHT: ISOMETRIC view (3/4 perspective)

Style requirements:
- Pure white background (#FFFFFF), no shadows on background
- Each view rendered as crisp black line art with subtle grey shading for depth
- Labels under each view in small clean sans-serif: "FRONT", "SIDE", "TOP", "ISO"
- Light grey center crosshair / construction lines on TOP view to show symmetry axis
- Thin grey dimension extension lines indicating major proportions (height, max width, neck width)
- Object orientation must be consistent across all four views
- Preserve the original object's PROPORTIONS exactly — do not stylize, simplify shape, or add decoration
- If the object has assembly seams, joints, or part divisions, draw them as thin solid lines
- No annotations, no text other than the four view labels
- Engineering blueprint aesthetic, like a CAD drawing exported to PDF

The goal is for another AI to reverse-engineer the 3D geometry from this sheet.`;

/** Run the full image → graph pipeline. Returns the parsed actions+reply once
 *  both stages succeed; throws (and emits error stage) on failure. */
export async function runVisionPipeline(req: VisionPipelineRequest): Promise<{ actions: ToolCall[]; reply: string; sheetDataUri: string }> {
  req.onStage({ kind: 'start' });

  // Convert the file once. Both stages need the same data URI.
  const photoDataUri = await fileToDataUri(req.imageFile);

  // ---- Stage 1: multi-view synthesis ----
  req.onStage({ kind: 'multiview-pending' });
  let sheet;
  try {
    sheet = await callFalImageEdit({
      apiKey: req.apiKey,
      imageUrls: [photoDataUri],
      prompt: MULTIVIEW_PROMPT,
      quality: 'high',
      imageSize: 'square_hd'
    });
  } catch (err) {
    const msg = (err as Error).message;
    req.onStage({ kind: 'error', stage: 'multiview', message: msg });
    throw err;
  }
  req.onStage({
    kind: 'multiview-done',
    sheetDataUri: sheet.dataUri,
    sheetUrl: sheet.url
  });

  // ---- Stage 2: vision graph synthesis ----
  req.onStage({ kind: 'synthesis-pending' });
  const ctx = req.conversationContext ? `Önceki konuşma:\n${req.conversationContext}\n\n` : '';
  const userPrompt = `${ctx}[KULLANICI] ${req.userPrompt}

Yukarıda iki görsel var:
1. Orijinal referans fotoğraf (kullanıcının yüklediği obje)
2. Senin için hazırlanmış teknik çizim sayfası (4 ortografik görünüm)

İki görseli birlikte analiz et. Teknik çizimden simetri ekseni, oranlar (yükseklik/çap, boyun/karın/taban yarıçapları), kademe sayısı ve assembly detaylarını çıkar. Sonra ClayForge node graph'ı kur — gerekiyorsa primitive.vase + transform/array node'larını birleştirerek objeyi yaklaşık olarak yeniden üret.

Çıkarımlarını <reply> içinde 1-2 cümleyle özetle ("yaklaşık 18cm yükseklik, 7cm karın çapı, 4 dilim..." gibi).`;

  let raw;
  try {
    raw = await callFalVision({
      apiKey: req.apiKey,
      model: req.model,
      systemPrompt: VISION_SYSTEM_PROMPT,
      prompt: userPrompt,
      imageUrls: [photoDataUri, sheet.dataUri],
      maxTokens: 4096
    });
  } catch (err) {
    const msg = (err as Error).message;
    req.onStage({ kind: 'error', stage: 'synthesis', message: msg });
    throw err;
  }

  const { actions, reply } = parseModelOutput(raw.output);
  req.onStage({ kind: 'synthesis-done', actions, reply });

  return { actions, reply, sheetDataUri: sheet.dataUri };
}
